
import React from 'react';
import { Flame, Tag, Clock } from 'lucide-react';
import TranslatedText from '@/TranslatedText';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

// Example hot deals data
const hotDeals = [
  {
    id: 1,
    title: 'Wireless Earbuds Pro',
    category: 'Electronics',
    originalPrice: 75000,
    salePrice: 45000,
    discountPercentage: 40,
    currency: 'RWF',
    imageUrl: '/placeholder.svg',
    endTime: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // 2 days from now
    trending: true
  },
  {
    id: 2,
    title: 'Premium Coffee Maker',
    category: 'Home & Living',
    originalPrice: 120000,
    salePrice: 89000,
    discountPercentage: 25,
    currency: 'RWF',
    imageUrl: '/placeholder.svg',
    endTime: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days from now
    trending: false
  },
  {
    id: 3,
    title: 'Traditional Woven Basket',
    category: 'Local Crafts',
    originalPrice: 35000,
    salePrice: 21000,
    discountPercentage: 40,
    currency: 'RWF',
    imageUrl: '/placeholder.svg',
    endTime: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000), // 1 day from now
    trending: true
  },
  {
    id: 4,
    title: 'Organic Skincare Set',
    category: 'Beauty & Wellness',
    originalPrice: 65000,
    salePrice: 45500,
    discountPercentage: 30,
    currency: 'RWF',
    imageUrl: '/placeholder.svg',
    endTime: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000), // 4 days from now
    trending: false
  }
];

const HotDeals: React.FC = () => {
  return (
    <div className="py-12">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-red-600 flex items-center">
          <Flame className="mr-2 text-red-500" size={28} />
          <TranslatedText text="Hot Deals" />
        </h2>
        <Link to="/search?tag=hot-deals">
          <Button variant="outline" className="text-ahavex-blue border-ahavex-blue">
            <TranslatedText text="View All" />
          </Button>
        </Link>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {hotDeals.map((deal) => (
          <Card key={deal.id} className="overflow-hidden hover:shadow-lg transition-shadow">
            <div className="relative pt-[100%]">
              <img 
                src={deal.imageUrl} 
                alt={deal.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute top-2 left-2">
                <Badge className="bg-red-600 hover:bg-red-700">
                  -{deal.discountPercentage}%
                </Badge>
              </div>
              {deal.trending && (
                <div className="absolute top-2 right-2">
                  <Badge className="bg-amber-500 hover:bg-amber-600">
                    <TranslatedText text="Trending" />
                  </Badge>
                </div>
              )}
              
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <div className="flex items-center text-white">
                  <Clock size={14} className="mr-1" />
                  <span className="text-xs font-medium">
                    <TranslatedText text="Ends in:" /> {formatDistanceToNow(deal.endTime, { addSuffix: false })}
                  </span>
                </div>
              </div>
            </div>
            
            <CardContent className="pt-4">
              <div className="text-sm text-gray-500 mb-1">
                <TranslatedText text={deal.category} />
              </div>
              <h3 className="font-semibold line-clamp-2 mb-2">
                <TranslatedText text={deal.title} />
              </h3>
              <div className="flex items-baseline gap-2">
                <span className="text-red-600 font-bold">
                  {deal.salePrice.toLocaleString()} {deal.currency}
                </span>
                <span className="text-gray-500 text-sm line-through">
                  {deal.originalPrice.toLocaleString()} {deal.currency}
                </span>
              </div>
            </CardContent>
            
            <CardFooter className="pt-0">
              <Button className="w-full bg-ahavex-blue hover:bg-ahavex-darkblue">
                <TranslatedText text="Shop Now" />
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default HotDeals;
